import type { components } from './api.types'
import type { RawDailyStatus } from './api'
import { RECORDING_OUTCOME } from './recordings'

/**
 * Reading the Daily Status payload, in exactly one place.
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 *
 * Two reasons, and the second is the important one.
 *
 * **The payload is untyped on the wire.** `DailyStatusPayload` is
 * `{"type": "object", "additionalProperties": true}` in the backend's OpenAPI
 * document, so `openapi-typescript` generates an alias with no fields. Every
 * product screen is built on that payload, so something has to describe it, and
 * that something is hand-written and therefore capable of being wrong. The
 * fixture test (§8.2) renders the backend's real `daily_result_EC.json` through
 * these types, which is the only thing that will notice the shape moving.
 *
 * **The client renders; it does not decide.** Every score, label, confidence
 * value, warning and disclaimer below is passed through verbatim. There is no
 * arithmetic in this file, no threshold, no rounding that changes a value, and no
 * string that describes a measurement. `eeg-tracking` owns scoring, the
 * thresholds and the forbidden-string scan; a client that recomputed any of it
 * would fork that governance into a place where nobody is checking it.
 *
 * Concretely, the rule that is easiest to break: **`null` is not zero.** Each of
 * the eight slots is `number | null`, and `null` means *not available for this
 * session*. A `?? 0` anywhere below would render a suppressed score as a real
 * measurement of nothing.
 */

/* ── the eight slots ─────────────────────────────────────────────────────── */

/**
 * The score slots, in the order `BrainBit_Pro_EEG_Status_Guide.docx` presents
 * them — signal quality first because it gates every other reading.
 *
 * **Iterate THIS, never the payload's `labels`.** `labels` is keyed only by the
 * slots that have a value: the canonical fixture holds seven entries because
 * `coupling_change` is null. A screen driven by `labels` renders seven cards and
 * silently loses the eighth's "not available for this session" state, which is
 * the one §1.4 asks for by name. `display` and `evidence` do carry all eight.
 */
export const SCORE_SLOTS = [
  'signal_quality',
  'spectral_slowing',
  'cognitive_engagement',
  'alpha_relaxation',
  'activation_arousal',
  'aperiodic_shift',
  'coupling_change',
  'state_stability',
] as const

export type ScoreSlot = (typeof SCORE_SLOTS)[number]

/**
 * Research-only and off by default.
 *
 * The Status Guide files `coupling_change` (wPLI) under "research-only statuses
 * (off by default)": on a low-sensor headset these timing measures are very
 * sensitive to processing and recording quality, and it must be described as a
 * timing-relationship change, never as "brain connectivity". The deployed backend
 * leaves `tracking_coupling_enabled` false, so the slot arrives null — but if a
 * flag is ever set, it renders as clearly experimental rather than being promoted
 * into the main readout.
 */
export const RESEARCH_ONLY_SLOTS: ReadonlySet<string> = new Set<ScoreSlot>(['coupling_change'])

/**
 * The schema version this client is BUILT against — what a freshly scored
 * recording carries, and what the fixtures under `src/test/fixtures` are.
 *
 * "1.5" since Sprint 1.9.9 W14: the backend pins eeg-tracking 0.11.0 (S2b), so a
 * day scored now is 1.5, and the backend's fixture copies moved with it. The
 * preserved stored-1.4 copy (`daily_result_EC_asym_schema14.json`) stays 1.4 and
 * stays readable through the literal below.
 */
export const SUPPORTED_SCHEMA_VERSION = '1.5'

/**
 * Every schema version this client can render.
 *
 * A recording keeps the schema it was scored with, forever: the daily result is
 * built once at scoring time and stored verbatim, and the backend's artifact
 * store raises rather than rewrite one. So a schema bump is retroactively
 * destructive unless the reader accepts what is already on disk — with a
 * single-value check, 1.3 -> 1.4 would turn every recording made before that
 * release into "this version of the app cannot read", which is a worse lie than
 * the one the check exists to prevent.
 *
 * Not a range and not a floor: an enumeration. 1.1 is absent and stays absent —
 * `daily_result_EC_schema11.json` is kept byte-for-byte as the guard that it
 * must still be refused.
 */
export const READABLE_SCHEMA_VERSIONS: ReadonlySet<string> = new Set([
  '1.3',
  // Literal, not `SUPPORTED_SCHEMA_VERSION`: every stored 1.4 day stays
  // readable on the day the constant moves to 1.5 (Sprint 1.9.9 D4).
  '1.4',
  // eeg-tracking 0.11.0 (Sprint 1.9.9): additive. `label_directions`,
  // `direction_labels` and the state map's `display` block are read by
  // `lib/home.ts`; nothing here depends on them.
  '1.5',
  SUPPORTED_SCHEMA_VERSION,
])

/**
 * The two — and only two — slots whose absence the payload explains.
 *
 * ── THIS IS READ BACK, NOT INFERRED ─────────────────────────────────────────
 *
 * `validity` is a set of booleans with no slot names in it, so "beta_valid is
 * false, therefore activation_arousal is missing" looks exactly like the client
 * deciding something. It is not: `eeg_tracking/config.py` defines
 * `VALIDITY_FAMILY_BY_SCORE` as this same mapping, and `result_writer.py` is what
 * acts on it — it nulls the score AND appends the token, in one loop, from that
 * dict. Reading the correspondence back is reading the package's own statement.
 *
 * **Only two families are gated, and the config says so in as many words**: the
 * low-frequency cards (`spectral_slowing`, `cognitive_engagement`,
 * `alpha_relaxation`, `aperiodic_shift`, `state_stability`) are robust to
 * high-frequency noise and are not gated at all. Extending this map to them would
 * be the client inventing an attribution, which is the thing acceptance criterion
 * 2 forbids — so it stays at two entries, and this comment is why.
 *
 * Both signals must agree before anything is attributed. If the package ever
 * changes the correspondence, the result is **no reason shown**, never a wrong
 * one.
 */
const SLOT_VALIDITY: Partial<Record<ScoreSlot, { flag: keyof DailyValidity; warning: string }>> = {
  activation_arousal: { flag: 'beta_valid', warning: 'beta_band_unreliable' },
  coupling_change: { flag: 'coupling_valid', warning: 'phase_lag_unreliable' },
}

/**
 * Every measurement each validity flag gates, read back from the package.
 *
 * eeg-tracking's config states these in three gate maps, and this is their
 * union per flag: `VALIDITY_FAMILY_BY_SCORE` (score slots),
 * `UI_VALIDITY_FAMILY_BY_SCORE` (the card set) and
 * `STATE_MAP_VALIDITY_FAMILY_BY_AXIS` (the state map). `coupling_valid` gates
 * `coupling_change` and nothing else; `beta_valid` gates `activation_arousal`,
 * the Readiness and Focus-stability cards and the state map's activation axis.
 * `low_frequency_valid` gates nothing by name, so it is not here and is always
 * shown.
 *
 * When this headband can produce NONE of what a flag gates, the flag is a
 * verdict on measurements that do not exist, and `beta_valid: true` beside them
 * would read as a check that passed.
 */
const GATED_BY: Partial<
  Record<ValidityFlag, { slots: readonly ScoreSlot[]; uiCards: readonly string[]; axes: readonly string[] }>
> = {
  beta_valid: { slots: ['activation_arousal'], uiCards: ['readiness', 'focus_stability'], axes: ['activation'] },
  coupling_valid: { slots: ['coupling_change'], uiCards: [], axes: [] },
}

/**
 * Whether a flag gates nothing this payload carries.
 *
 * The ANCHOR is `validity.unavailable`: every slot the flag gates must be one
 * the payload says THIS DEVICE cannot produce. That is what keeps a Pro day
 * unchanged -- there, a flag that failed nulls its measurements itself, and a
 * `beta_valid: false` beside those nulls is their explanation, never noise.
 * Given the anchor, a gated card or axis with no number is not there to judge.
 */
function gatesNothing(flag: ValidityFlag, validity: DailyValidity, research: DailyStatusView['research']): boolean {
  const gated = GATED_BY[flag]
  if (gated === undefined) return false
  if (!gated.slots.every((slot) => validity.unavailable?.[slot] !== undefined)) return false
  const uiScores = isObject(research.uiScores) ? research.uiScores.scores : undefined
  const cardsAbsent = gated.uiCards.every(
    (card) => numberOrNull(isObject(uiScores) ? uiScores[card] : undefined) === null,
  )
  const stateMap: unknown = research.stateMap
  const axesAbsent = gated.axes.every((axis) => {
    const entry = isObject(stateMap) ? stateMap[axis] : undefined
    return numberOrNull(isObject(entry) ? entry.value : undefined) === null
  })
  return cardsAbsent && axesAbsent
}

/** The three per-band booleans `validity` carries, in the payload's order. */
export const VALIDITY_FLAGS = ['low_frequency_valid', 'beta_valid', 'coupling_valid'] as const
export type ValidityFlag = (typeof VALIDITY_FLAGS)[number]

/**
 * The per-band flags worth showing, as `[key, value]` in `VALIDITY_FLAGS` order.
 *
 * A flag the payload did not send is absent (never defaulted), and a flag that
 * gates nothing this headband produced is left out — see `GATED_BY` and
 * `gatesNothing`. Both are lookups in the payload; nothing here judges a band.
 */
export function shownValidityFlags(
  status: Pick<DailyStatusView, 'validity' | 'research'>,
): ReadonlyArray<readonly [ValidityFlag, boolean]> {
  const { validity, research } = status
  if (validity === null) return []
  return VALIDITY_FLAGS.flatMap((key) => {
    const value = validity[key]
    if (typeof value !== 'boolean') return []
    if (gatesNothing(key, validity, research)) return []
    return [[key, value] as const]
  })
}

/* ── the blocks, as the payload carries them ─────────────────────────────── */

/** `display[slot]` — the backend's own words for a card. */
export interface ScoreDisplay {
  title: string
  description: string
  evidence_tier: string
  /**
   * `display[slot].scale_baseline`, the payload's own mark on this slot's
   * scale.
   *
   * Absent on every payload the deployed backend produces (backend ask B1), so
   * `null` and no mark is the normal case. It is read here rather than assumed
   * because there is no client constant available to assume: six slots are
   * `clip(50 + 15z)`, but `signal_quality` and `state_stability` use a
   * (100, -25) scale where 100 is the undisturbed value and 50 means nothing
   * at all (`eeg_tracking/config.py:371-374`). NEVER a client constant — O2.
   *
   * Since schema 1.4 the six default-scaled slots carry it and the two
   * quality-scaled ones deliberately do not, so `null` now means "this scale
   * has no meaningful midpoint" rather than "the backend does not send this".
   */
  scaleBaseline?: number | null
  /**
   * `display[slot].scale_baseline_label` — the words that NAME the mark.
   *
   * Present exactly when `scaleBaseline` is. The client may not author them:
   * choosing a word for what the position means is a scientific claim, and
   * `TrendChart.test.tsx` forbids the chart body saying "baseline" for that
   * reason. So the mark renders unnamed rather than named by us if this is
   * absent.
   */
  scaleBaselineLabel?: string | null
  /**
   * `display[slot].short_title` — the same measure, named for a control rather
   * than for a sentence. Several `title`s run past seventy characters, which a
   * native `<select>` truncates unpredictably per platform.
   *
   * Every reader falls back to `title`, so a payload without it degrades to
   * today's behaviour rather than breaking.
   */
  shortTitle?: string | null
  /**
   * `display[slot].user_title` — the measure's plain, user-facing name
   * (eeg-tracking 0.14.0). Carried on the six reviewed measures and not on
   * `signal_quality` or `coupling_change`. When present the card shows it as
   * the main name and keeps `title` (the technical label) beside it, smaller.
   *
   * `undefined` when absent, blank or not a string, and the card then renders
   * exactly as it did before 0.14.0. The words are the package's: the client
   * never derives a plain name from `title`.
   */
  userTitle?: string
  /**
   * `display[slot].user_summary` — the package's one-line "what it tells you"
   * for that plain name (eeg-tracking 0.14.0). Same rule as `userTitle`.
   */
  userSummary?: string
}

/** `evidence[slot]` — the longer disclosure behind a card. */
export interface ScoreEvidence {
  title: string
  state_change: string
  meaning: string
  literature: string
  /** What this score is NOT. §Notes: surface it beside the number, not in a footer. */
  precaution: string
  refs: string[]
}

/** `validity` — per-band flags plus free-text reasons. */
export interface DailyValidity {
  low_frequency_valid?: boolean
  beta_valid?: boolean
  coupling_valid?: boolean
  alpha_reactivity?: string | null
  reasons?: string[]
  /**
   * `validity.unavailable` — slot → reason token, for a measurement THIS DEVICE
   * can never produce (eeg-tracking 0.9.0, Sprint 1.9.2 S2d-4). Present only on
   * a recording whose montage lacks the electrodes a measure needs — today the
   * four-channel BrainBit — and absent on every BrainBit Pro payload, which is
   * what keeps the Pro payload byte-identical.
   */
  unavailable?: Readonly<Record<string, string>>
  /**
   * `validity.caveats` — slot → reason token, for a measurement that IS shipped
   * but carries a documented reservation. Deliberately a separate map from
   * `unavailable`: collapsing them would either hide a real number or present a
   * caveated one as unqualified.
   */
  caveats?: Readonly<Record<string, string>>
  /**
   * `validity.withheld` — slot → reason token, for a measurement THIS HEADBAND
   * produces but that THIS RECORDING withheld, because its signal quality was
   * too low to compare it with the baseline (eeg-tracking 0.13.0, FIRST-DAY-01;
   * today the one token is `low_signal_quality`). Present only when non-empty.
   *
   * Deliberately NOT `unavailable`: that map says no recording from this device
   * will ever carry the number, and the screen acts on it (no dial, no
   * `beta_valid` flag). A withheld measure would read the next day. So the two
   * are separate maps, and the card renders them as separate classes.
   */
  withheld?: Readonly<Record<string, string>>
  /**
   * `validity.reason_text` — reason token → the sentence it stands for
   * (eeg-tracking 0.10.0). One entry per token that appears under
   * `unavailable`, `caveats` or `withheld` in THIS payload. The words are the package's, scanned by its
   * live forbidden-string gate; the client renders them and never writes its own.
   */
  reason_text?: Readonly<Record<string, string>>
}

/**
 * One slot's reason, as the payload states it: the token, and the sentence
 * `validity.reason_text` pairs with it.
 *
 * `sentence` is `null` when the payload carries the token without its words —
 * a payload from eeg-tracking 0.9.0, before `reason_text` existed. The screen
 * then shows the raw token rather than nothing and rather than words of its own.
 */
export interface SlotReason {
  token: string
  sentence: string | null
}

export type DailyBaselineStatus = components['schemas']['DailyBaselineStatus']
export type UiScores = components['schemas']['UiScores']
export type StateMap = components['schemas']['StateMap']
export type BalanceScore = components['schemas']['BalanceScore']

/* ── the read model ──────────────────────────────────────────────────────── */

/**
 * One card's worth of payload, assembled but not interpreted.
 *
 * `value === null` is the whole point of the shape: it is a state, not a missing
 * field, and the component that renders this must have no way to confuse it with
 * a number. There is deliberately no `valueOrZero`, no `displayValue: string`
 * and no `percent`.
 */
export interface ScoreCard {
  slot: ScoreSlot
  /** Verbatim. `null` means *not available for this session*. */
  value: number | null
  /** The backend's label for this value, e.g. "around baseline". Null when the
   *  slot has no value, because `labels` then carries no key for it. */
  label: string | null
  /**
   * `label_codes[slot]` — the same reading as a token: `around | mild | strong`.
   *
   * What it replaces: until schema 1.4 the client had to MATCH THE PROSE to
   * choose a tone, which is why `labelTone.ts` had to write the backend's own
   * sentences down and why it was the single exemption in the governed-string
   * scan. Keying on the token retires that outright.
   *
   * Null on a recording scored before 1.4 — `labelTone` then falls back to its
   * neutral default rather than guessing from the words.
   */
  labelCode: string | null
  display: ScoreDisplay | null
  evidence: ScoreEvidence | null
  researchOnly: boolean
  /**
   * The payload's own warning token for why this slot has no value — §1.4's
   * "the reason from `validity`/`active_warnings` where one exists".
   *
   * Empty for six of the eight slots even when they are unavailable, because the
   * package gates only two families and inventing a reason for the rest would be
   * worse than the honest silence. See `SLOT_VALIDITY`.
   */
  unavailableReasons: string[]
  /**
   * `validity.unavailable[slot]` — this DEVICE cannot produce this measurement,
   * in the payload's own words (`validity.reason_text`).
   *
   * A different statement from `unavailableReasons`: those say THIS SESSION's
   * reading could not be trusted; this says no recording from this headband
   * will ever carry the number, because the montage lacks the electrodes the
   * measure is computed from. When it is set `value` is `null` whatever the
   * slot carried — see `readDailyStatus`.
   */
  unavailable: SlotReason | null
  /**
   * `validity.withheld[slot]` — this recording's signal quality was too low to
   * compare the measure with the baseline, in the payload's own words. Set only
   * on a slot with no value.
   *
   * ONE SLOT, ONE REASON. A slot already explained is not explained twice, so
   * this is null when the slot carries a device reason (`unavailable`, which
   * stays true of every recording from this headband) or a gated warning token
   * (`unavailableReasons`, the package's own two-signal attribution, which the
   * row renders beside the card's sentence). It is set only where the slot
   * would otherwise say nothing but "Not available for this session".
   */
  withheld: SlotReason | null
  /**
   * `validity.caveats[slot]` — the number IS shipped, with a reservation the
   * payload states. Rendered beside the number, never instead of it.
   */
  caveat: SlotReason | null
}

export interface DailyStatusView {
  readable: true
  schemaVersion: string
  sessionId: string | null
  sessionDate: string | null
  /** All eight, always, in `SCORE_SLOTS` order. */
  scores: ScoreCard[]
  summary: string | null
  /** The payload's own number. Not a percentage, not a grade, not rescaled. */
  confidence: number | null
  lowConfidence: boolean
  activeWarnings: string[]
  validity: DailyValidity | null
  /** Rendered, never paraphrased. */
  disclaimer: string | null
  baselineStatus: DailyBaselineStatus | null
  /**
   * The feature-gated blocks, present only when the backend's flags were on.
   * Absent on every deployed dev payload today. Render as clearly experimental.
   */
  research: {
    uiScores: UiScores | null
    stateMap: StateMap | null
    balanceScore: BalanceScore | null
    /**
     * `raw.advanced`, present when `compute_advanced_features` is on. Untyped in
     * the contract, so it is carried as-is and rendered as-is. It was parsed by
     * nothing at all until Stage 7c, which is why it is a bare record.
     */
    advanced: Record<string, unknown> | null
  }
}

/** One research block, flattened to rows in the payload's own spelling. */
export interface ResearchBlock {
  /** The wire key -- `state_map`, not a title this client invented. */
  key: string
  entries: ReadonlyArray<readonly [string, unknown]>
}

/**
 * The research blocks as flat key/value rows.
 *
 * ── WHY THE WIRE SPELLING, AND NOT A LABEL ────────────────────────────────
 *
 * These blocks carry no `display` block and no labels: `state_map` holds
 * `quadrant_reason: "near_baseline_dead_zone"` and `badge: "research_only"`,
 * which are backend tokens rather than sentences for a person. Criterion 2
 * forbids invented result labels, so the honest rendering is the payload's own
 * keys and the payload's own values, and the §Notes requirement that they read
 * as "clearly experimental" is carried by the dashed section they sit in.
 *
 * An empty object contributes nothing: a block that arrived with no fields is
 * not evidence of anything, and a heading over an empty list is the defect this
 * function exists to end.
 */
export function researchBlocks(status: DailyStatusView): ResearchBlock[] {
  const out: ResearchBlock[] = []
  const add = (key: string, value: unknown) => {
    if (!isObject(value)) return
    const entries = Object.entries(value)
    if (entries.length > 0) out.push({ key, entries })
  }
  add('state_map', status.research.stateMap)
  add('balance_score', status.research.balanceScore)
  add('ui_scores', status.research.uiScores)
  add('advanced', status.research.advanced)
  return out
}

/** One row of a research block, as the screen renders it. */
export type ResearchRow =
  | { field: string; kind: 'value'; value: unknown }
  /** A measurement the payload did not produce, and the payload's reason. */
  | { field: string; kind: 'unavailable'; reason: SlotReason }

/**
 * A research block's rows, with every unavailable measurement as its reason.
 *
 * ── A NULL WITH A REASON IS A SENTENCE, NEVER THE WORD `null` ──────────────
 *
 * On a four-channel day the state map's axes and the balance score arrive as
 * `{ value: null, reason: "requires_frontal_electrodes" }`, and rendering the
 * rows as they came printed `null` beside a raw token. Three shapes in the
 * payload say "this measurement has no value, and here is why", and each is
 * read as exactly that:
 *
 * - the BLOCK is the measurement (`balance_score`): its `value` row carries the
 *   reason and its `reason` row goes, having been said;
 * - a FIELD is the measurement (a state-map axis): the object collapses to its
 *   reason;
 * - a null field with a `<field>_reason` sibling (`quadrant`): the same.
 *
 * The words are `validity.reason_text[token]`; a token with no sentence is
 * returned as the token, for the screen to show as a tag. Lookups, all of it:
 * the payload names the reason, and the payload names its words.
 */
export function researchRows(block: ResearchBlock, validity: DailyValidity | null): ResearchRow[] {
  const fields = new Map(block.entries)
  const reasonOf = (token: unknown): SlotReason | null =>
    typeof token === 'string' && token !== ''
      ? { token, sentence: validity?.reason_text?.[token] ?? null }
      : null

  const blockReason = fields.has('value') && fields.get('value') === null ? reasonOf(fields.get('reason')) : null
  const consumed = new Set<string>()
  if (blockReason !== null) consumed.add('reason')
  for (const [field, value] of block.entries) {
    if (value === null && reasonOf(fields.get(`${field}_reason`)) !== null) consumed.add(`${field}_reason`)
  }

  return block.entries.flatMap(([field, value]): ResearchRow[] => {
    if (consumed.has(field)) return []
    if (field === 'value' && blockReason !== null) return [{ field, kind: 'unavailable', reason: blockReason }]
    if (value === null) {
      const sibling = reasonOf(fields.get(`${field}_reason`))
      if (sibling !== null) return [{ field, kind: 'unavailable', reason: sibling }]
    }
    if (isObject(value) && 'value' in value && value.value === null) {
      const own = reasonOf(value.reason)
      if (own !== null) return [{ field, kind: 'unavailable', reason: own }]
    }
    return [{ field, kind: 'value', value }]
  })
}

export type UnavailableReason =
  /** Not an object at all, or missing the fields that make it a daily result. */
  | 'malformed'
  /** `schema_version` is not one this build knows how to read. */
  | 'unreadable_schema'
  /** `preview: true` — an ingest preview, never the persisted conclusion. */
  | 'preview'
  /** `shippable: false` — the package itself says do not show this. */
  | 'not_shippable'

export interface DailyStatusUnavailable {
  readable: false
  reason: UnavailableReason
  /** For the screen to render. Says what is wrong, never invents a measurement. */
  detail: string
}

export type DailyStatusResult = DailyStatusView | DailyStatusUnavailable

/* ── narrowing helpers. Structural only; nothing here judges a value. ────── */

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** A finite number, or null. A non-finite one is not a measurement. */
const numberOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const stringOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null)

/** An optional word: a non-blank string, or absent. A blank name is no name. */
const wordOrUndefined = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined

const stringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []

function displayFor(block: unknown, slot: ScoreSlot): ScoreDisplay | null {
  if (!isObject(block)) return null
  const entry = block[slot]
  if (!isObject(entry)) return null
  const title = stringOrNull(entry.title)
  if (title === null) return null
  const display: ScoreDisplay = {
    title,
    description: stringOrNull(entry.description) ?? '',
    evidence_tier: stringOrNull(entry.evidence_tier) ?? '',
    scaleBaseline: numberOrNull(entry.scale_baseline),
    scaleBaselineLabel: stringOrNull(entry.scale_baseline_label),
    shortTitle: stringOrNull(entry.short_title),
  }
  // Optional, and set only when present, so a payload without them parses to
  // exactly the object it did before eeg-tracking 0.14.0.
  const userTitle = wordOrUndefined(entry.user_title)
  if (userTitle !== undefined) display.userTitle = userTitle
  const userSummary = wordOrUndefined(entry.user_summary)
  if (userSummary !== undefined) display.userSummary = userSummary
  return display
}

function evidenceFor(block: unknown, slot: ScoreSlot): ScoreEvidence | null {
  if (!isObject(block)) return null
  const entry = block[slot]
  if (!isObject(entry)) return null
  const title = stringOrNull(entry.title)
  if (title === null) return null
  return {
    title,
    state_change: stringOrNull(entry.state_change) ?? '',
    meaning: stringOrNull(entry.meaning) ?? '',
    literature: stringOrNull(entry.literature) ?? '',
    precaution: stringOrNull(entry.precaution) ?? '',
    refs: stringArray(entry.refs),
  }
}

function labelFor(block: unknown, slot: ScoreSlot): string | null {
  if (!isObject(block)) return null
  return stringOrNull(block[slot])
}

function validityOf(raw: Record<string, unknown>): DailyValidity | null {
  const block = raw.validity
  if (!isObject(block)) return null
  const validity: DailyValidity = {}
  if (typeof block.low_frequency_valid === 'boolean')
    validity.low_frequency_valid = block.low_frequency_valid
  if (typeof block.beta_valid === 'boolean') validity.beta_valid = block.beta_valid
  if (typeof block.coupling_valid === 'boolean') validity.coupling_valid = block.coupling_valid
  if (typeof block.alpha_reactivity === 'string') validity.alpha_reactivity = block.alpha_reactivity
  const reasons = stringArray(block.reasons)
  if (reasons.length > 0) validity.reasons = reasons
  // The four maps are emitted only when non-empty, so an empty one is carried
  // as absent -- the same shape the package writes.
  const unavailable = stringMap(block.unavailable)
  if (unavailable !== null) validity.unavailable = unavailable
  const caveats = stringMap(block.caveats)
  if (caveats !== null) validity.caveats = caveats
  const withheld = stringMap(block.withheld)
  if (withheld !== null) validity.withheld = withheld
  const reasonText = stringMap(block.reason_text)
  if (reasonText !== null) validity.reason_text = reasonText
  return validity
}

/**
 * A map of non-empty strings, or null when nothing in it is one.
 *
 * An entry whose value is not a non-empty string is dropped rather than coerced:
 * an empty token would render as a blank where a reason belongs, and a blank is
 * the one rendering an unavailable measurement must never get.
 */
function stringMap(v: unknown): Record<string, string> | null {
  if (!isObject(v)) return null
  const entries = Object.entries(v).filter(
    (pair): pair is [string, string] => typeof pair[1] === 'string' && pair[1] !== '',
  )
  return entries.length === 0 ? null : Object.fromEntries(entries)
}

/**
 * One slot's entry in `validity.unavailable`, `validity.caveats` or
 * `validity.withheld`, with its
 * sentence from `validity.reason_text` when the payload carries one.
 *
 * A LOOKUP, twice, and nothing else: which slot a token belongs to and which
 * words a token stands for are both stated by the payload. Nothing here decides
 * that a measurement is unavailable.
 */
function slotReason(
  map: Readonly<Record<string, string>> | undefined,
  slot: ScoreSlot,
  validity: DailyValidity | null,
): SlotReason | null {
  const token = map?.[slot]
  if (token === undefined) return null
  return { token, sentence: validity?.reason_text?.[token] ?? null }
}

/**
 * One `ui_scores` card's entry in `validity.unavailable`, with its sentence:
 * `slotReason` with the card's own key. eeg-tracking 0.11.0 (C8 (e)) names the
 * four interface cards there on a headband that cannot produce them (the
 * classic BrainBit); `ui_scores` keys are not `ScoreSlot`s, hence the string.
 *
 * A LOOKUP and nothing more: the card reads only its own key, so a measure's
 * token never explains a card.
 */
export function uiCardUnavailable(validity: DailyValidity | null, card: string): SlotReason | null {
  const token = validity?.unavailable?.[card]
  if (token === undefined) return null
  return { token, sentence: validity?.reason_text?.[token] ?? null }
}

/**
 * The warning tokens that explain one unavailable slot.
 *
 * Both signals must agree: the flag is false AND the token the package pairs with
 * it is in `active_warnings`. Requiring both is what makes this a read-back
 * rather than a deduction — if the package ever stops emitting one of the two,
 * the honest outcome is no reason rather than a reason we assembled ourselves.
 */
function reasonsFor(
  slot: ScoreSlot,
  validity: DailyValidity | null,
  warnings: readonly string[],
): string[] {
  const gate = SLOT_VALIDITY[slot]
  if (gate === undefined || validity === null) return []
  if (validity[gate.flag] !== false) return []
  return warnings.includes(gate.warning) ? [gate.warning] : []
}

function baselineStatusOf(raw: Record<string, unknown>): DailyBaselineStatus | null {
  const block = raw.baseline_status
  if (!isObject(block)) return null
  if (typeof block.provisional !== 'boolean') return null
  if (typeof block.days_confirmed_remaining !== 'number') return null
  const display = stringOrNull(block.display)
  if (display === null) return null
  return {
    provisional: block.provisional,
    days_confirmed_remaining: block.days_confirmed_remaining,
    display,
  }
}

/**
 * Read a persisted Daily Status payload.
 *
 * Returns either a view whose every field came from the payload, or an explicit
 * unavailable state. It never returns a partially-invented view: §1.7 says an
 * unusable payload "gets a clear unavailable state rather than scores", and a
 * half-rendered readout is exactly the thing that reads as a measurement.
 */
export function readDailyStatus(raw: RawDailyStatus | unknown): DailyStatusResult {
  if (!isObject(raw)) {
    return {
      readable: false,
      reason: 'malformed',
      detail: 'This result could not be read.',
    }
  }

  const schemaVersion = stringOrNull(raw.schema_version)
  if (schemaVersion === null) {
    return {
      readable: false,
      reason: 'malformed',
      detail: 'This result could not be read: it carries no schema version.',
    }
  }
  if (!READABLE_SCHEMA_VERSIONS.has(schemaVersion)) {
    // Refusing rather than guessing. The backend refuses a foreign
    // `schema_version` for the same reason: a daily result IS the product's
    // conclusion, and shipping one the app cannot read is worse than shipping
    // none. What it does NOT do is refuse a schema this client simply predates
    // reading — see READABLE_SCHEMA_VERSIONS.
    return {
      readable: false,
      reason: 'unreadable_schema',
      detail:
        `This result was produced with result format ${schemaVersion}, which this ` +
        'version of the app cannot read.',
    }
  }

  // §1.7. A preview is what an ingest returns before anything is persisted; it is
  // never the result, and the one thing this screen must never do is show one.
  if (raw.preview === true) {
    return {
      readable: false,
      reason: 'preview',
      detail: 'This result is a preview and is not the recorded outcome.',
    }
  }
  if (raw.shippable === false) {
    return {
      readable: false,
      reason: 'not_shippable',
      detail: 'This recording did not produce a result that can be shown.',
    }
  }

  const validity = validityOf(raw)
  const warnings = stringArray(raw.active_warnings)

  const scores: ScoreCard[] = SCORE_SLOTS.map((slot) => {
    const unavailable = slotReason(validity?.unavailable, slot, validity)
    // ── THE PAYLOAD'S OWN STATEMENT WINS OVER A STRAY NUMBER ────────────────
    // `validity.unavailable` says this headband cannot produce this measure,
    // and the package nulls the slot in the same breath. If a number ever
    // arrived beside the token anyway, it would be the nanmean-over-what-is-left
    // the package suppresses the slot to avoid — computed from a different set
    // of inputs than the name promises. So it is not shown as a measurement.
    // This follows the payload's word; it judges no value.
    const value = unavailable === null ? numberOrNull(raw[slot]) : null
    const unavailableReasons = value === null ? reasonsFor(slot, validity, warnings) : []
    // A withheld reason explains a MISSING number, so a number beside the token
    // is shown as a number with no reason. One slot, one reason: see
    // `ScoreCard.withheld` for why a device reason or a gated token wins.
    const withheld =
      value === null && unavailable === null && unavailableReasons.length === 0
        ? slotReason(validity?.withheld, slot, validity)
        : null
    return {
      slot,
      value,
      // Only where the payload has one. A slot with no value has no key in
      // `labels`, and inventing "—" here would put a client-authored string
      // where a measured one belongs.
      label: value === null ? null : labelFor(raw.labels, slot),
      // Same gate as `label`, and deliberately the same one: the backend makes
      // both maps sparse in exactly the places a score was suppressed, so a
      // reading beside a blank number cannot arise on either side.
      labelCode: value === null ? null : labelFor(raw.label_codes, slot),
      display: displayFor(raw.display, slot),
      evidence: evidenceFor(raw.evidence, slot),
      researchOnly: RESEARCH_ONLY_SLOTS.has(slot),
      unavailableReasons,
      unavailable,
      withheld,
      // A caveat qualifies a number, so a slot with none has nothing to qualify.
      caveat: value === null ? null : slotReason(validity?.caveats, slot, validity),
    }
  })

  return {
    readable: true,
    schemaVersion,
    sessionId: stringOrNull(raw.session_id),
    sessionDate: stringOrNull(raw.session_date),
    scores,
    summary: stringOrNull(raw.summary),
    confidence: numberOrNull(raw.confidence),
    // A missing flag is treated as LOW confidence, not high. The failure that
    // matters is showing a reading as more dependable than it is.
    lowConfidence: raw.low_confidence_flag !== false,
    activeWarnings: warnings,
    validity,
    disclaimer: stringOrNull(raw.disclaimer),
    baselineStatus: baselineStatusOf(raw),
    research: {
      uiScores: isObject(raw.ui_scores) ? (raw.ui_scores as unknown as UiScores) : null,
      stateMap: isObject(raw.state_map) ? (raw.state_map as unknown as StateMap) : null,
      balanceScore: isObject(raw.balance_score)
        ? (raw.balance_score as unknown as BalanceScore)
        : null,
      advanced: isObject(raw.advanced) ? (raw.advanced as Record<string, unknown>) : null,
    },
  }
}

/**
 * Whether a recording can have a persisted Daily Status at all.
 *
 * `empty` never had one — the recording carried no scoreable signal, was never
 * scored, and `GET /recordings/{id}/daily-status` answers 404. `failed` never got
 * that far either; its user-facing detail is the terminal Job's Problem. Asking
 * for those is not merely wasteful, it manufactures a 404 that looks like an
 * error to a user whose recording simply had nothing in it.
 */
export function hasDailyStatus(status: string): boolean {
  // Read from the recording vocabulary rather than restated here. The same fact
  // written down twice is the same fact until somebody edits one of them.
  return RECORDING_OUTCOME[status]?.hasResult === true
}
